import React, { useState } from 'react';
import ModalSheet from "../../../../components/atoms/modalSheet";
import Spinner from "../../../../components/atoms/spinner";
import { IonInput, IonButton, IonSegment, IonSegmentButton, IonLabel, useIonAlert } from "@ionic/react";
import { GetPointData } from "../../../../services/API";
import './FilterModal.css';
import { getBaladyAccessToken } from '../../../../services/API/baladyService';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  region: { key: string; name: string };
  city: { key: string; name: string };
  district: { key: string; name: string };
  plan: { key: string; name: string };
  parcel: { key: string; name: string };
  onRegionClick: () => void;
  onCityClick: () => void;
  onDistrictClick: () => void;
  onPlanClick: () => void;
  onParcelClick: () => void;
  onGoToCoordinates?: (rings: any) => void;
  onOpenInfoModal?: (data: any) => void;
  // Add flags to check if data exists
  hasCities?: boolean;
  hasDistricts?: boolean;
  hasPlans?: boolean;
  hasParcels?: boolean;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  isLoading,
  region: {key:regionKey, name:regionName},
  city: {key:cityKey, name:cityName},
  district: {key:districtKey, name:districtName},
  plan: {key:planKey, name:planName},
  parcel: {key:parcelKey, name:parcelName},
  onRegionClick,
  onCityClick,
  onDistrictClick,
  onPlanClick,
  onParcelClick,
  onGoToCoordinates,
  onOpenInfoModal,
  hasCities = false,
  hasDistricts = false,
  hasPlans = false,
  hasParcels = false
}) => {
  const [activeTab, setActiveTab] = useState<'advanced' | 'coordinates'>('advanced');
  const [coordinateData, setCoordinateData] = useState<any>(null);
  const [coordinateLoading, setCoordinateLoading] = useState(false);
  const [longitude, setLongitude] = useState('');
  const [latitude, setLatitude] = useState('');
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [coordinateSearchMethod, setCoordinateSearchMethod] = useState<'manual' | 'googleMaps'>('manual');
  const [presentAlert] = useIonAlert();

  const handleCoordinateSearch = async () => {
    let searchLongitude = longitude;
    let searchLatitude = latitude;

    if (coordinateSearchMethod === "googleMaps") {
      if (!googleMapsUrl) {
        setGoogleMapsUrl("");
        setCoordinateData(null);
        presentAlert({
          header: "تنبيه",
          message: "يرجى إدخال رابط خرائط جوجل",
          buttons: ["موافق"]
        });
        return;
      }

      // Extract coordinates from Google Maps URL
      const coordinates = extractCoordinatesFromGoogleMapsUrl(googleMapsUrl);
      if (!coordinates) {
        setGoogleMapsUrl("");
        setCoordinateData(null);
        presentAlert({
          header: "تنبيه",
          message: "لم يتم العثور على إحداثيات صحيحة في الرابط",
          buttons: ["موافق"]
        });
        return;
      }

      searchLongitude = coordinates.longitude;
      searchLatitude = coordinates.latitude;
    } else {
      if (!longitude || !latitude) {
        setLongitude("");
        setLatitude("");
        setCoordinateData(null);
        presentAlert({
          header: "تنبيه",
          message: "يرجى إدخال خط الطول وخط العرض",
          buttons: ["موافق"]
        });
        return;
      }
    }

    setCoordinateLoading(true);
    try {
      // You'll need to get the token from your auth context or props
      const token = await getBaladyAccessToken();      
      const response = await GetPointData(token, searchLongitude, searchLatitude);
      
      if (response.data?.data?.result) {
        setCoordinateData(response.data.data.result);
      } else {
        setLongitude("");
        setLatitude("");
        setGoogleMapsUrl("");
        setCoordinateData(null);  
        presentAlert({
          header: "تنبيه",
          message: "لم يتم العثور على بيانات لهذه الإحداثيات",
          buttons: ["موافق"]
        });
      }
    } catch (error: any) {
      console.error('Coordinate search error:', error);
      setLongitude("");
      setLatitude("");
      setGoogleMapsUrl("");
      setCoordinateData(null);
      presentAlert({
        header: "خطأ",
        message: error?.response?.data?.data?.responseMessage || "حدث خطأ أثناء البحث",
        buttons: ["موافق"]
      });
    } finally {
      setCoordinateLoading(false);
    }
  };

  // Helper function to extract coordinates from Google Maps URL
  const extractCoordinatesFromGoogleMapsUrl = (url: string) => {
    try {
      // Handle different Google Maps URL formats
      let match;
      
      // Format: https://maps.google.com/?q=lat,lng
      match = url.match(/[?&]q=([^&]+)/);
      if (match) {
        const coords = match[1].split(',');
        if (coords.length === 2) {
          const lat = parseFloat(coords[0]);
          const lng = parseFloat(coords[1]);
          if (!isNaN(lat) && !isNaN(lng)) {
            return { latitude: lat.toString(), longitude: lng.toString() };
          }
        }
      }

      // Format: https://www.google.com/maps/place/.../@lat,lng,zoom
      match = url.match(/@([^\/]+)/);
      if (match) {
        const coords = match[1].split(',');
        if (coords.length >= 2) {
          const lat = parseFloat(coords[0]);
          const lng = parseFloat(coords[1]);
          if (!isNaN(lat) && !isNaN(lng)) {
            return { latitude: lat.toString(), longitude: lng.toString() };
          }
        }
      }

      // Format: https://maps.google.com/?ll=lat,lng
      match = url.match(/[?&]ll=([^&]+)/);
      if (match) {
        const coords = match[1].split(',');
        if (coords.length === 2) {
          const lat = parseFloat(coords[0]);
          const lng = parseFloat(coords[1]);
          if (!isNaN(lat) && !isNaN(lng)) {
            return { latitude: lat.toString(), longitude: lng.toString() };
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error extracting coordinates:', error);
      return null;
    }
  };

  const handleGoToCoordinates = () => {
    if (coordinateData?.geometry?.rings) {
      onGoToCoordinates?.(coordinateData?.geometry?.rings);
      onClose();
    }
  };

  const handleOpenInfoModal = () => {
    if (coordinateData) {
      onOpenInfoModal?.(coordinateData);
      onClose();
    }
  };

  return (
    <ModalSheet
      isOpen={isOpen}
      initialBreakpoint={0.8}
      breakpoints={[0, 0.25, 0.5, 0.75]}
      onDidDismiss={onClose}
      close={onClose}
    >
      <div className="w-full h-full pt-4 flex flex-col justify-start items-center animate__animated animate__rotateInUpLeft">
        {isLoading ? <Spinner color={"primary"} size={"large"} /> : null}

        <div className="w-full flex justify-between items-center mb-4">
          <p className="font-bold text-primaryColor100 text-2xl">
            البحث المتقدم
          </p>
          <IonButton
            fill="solid"
            onClick={onClose}
            className="text-danger-500"
            color="danger"
            style={{
              backgroundColor: "#ef4444",
              border: "2px solid #dc2626",
              borderRadius: "8px",
            }}
          >
            إغلاق
          </IonButton>
        </div>

        {/* Tab Navigation */}
        <div className="w-full mb-4">
          <IonSegment
            value={activeTab}
            onIonChange={(e) =>
              setActiveTab(e.detail.value as "advanced" | "coordinates")
            }
            className="w-full"
          >
            <IonSegmentButton value="advanced" className="text-primaryColor100">
              <IonLabel className="text-lg font-bold">البحث الهرمى</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton
              value="coordinates"
              className="text-primaryColor100"
            >
              <IonLabel className="text-lg font-bold">
                البحث بالإحداثيات
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </div>

        {/* Advanced Search Tab */}
        {activeTab === "advanced" && (
          <>
            <div className="relative w-full mt-4">
              <IonInput
                readonly={true}
                disabled={isLoading}
                placeholder="يرجى اختيار المنطقة"
                value={isLoading && !regionName ? "جاري التحميل..." : regionName}
                onClick={!isLoading ? onRegionClick : undefined}
                className={`w-full border-2 rounded border-paragraphColor text-2xl text-primaryColor100 ${isLoading ? 'opacity-50' : ''}`}
              ></IonInput>
              <label className="absolute text-xl font-bold top-2 z-10 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                المنطقة
              </label>
            </div>

            {/* City */}
            <div className="relative w-full mt-4">
              <IonInput
                readonly={true}
                disabled={!regionKey || isLoading || (!!regionKey && !hasCities)}
                placeholder={!regionKey ? "يرجى اختيار المنطقة أولاً" : "يرجى اختيار المدينة"}
                value={isLoading && !!regionKey && !cityName ? "جاري التحميل..." : cityName}
                onClick={!!regionKey && !isLoading && hasCities ? onCityClick : undefined}
                className={`w-full border-2 rounded border-paragraphColor text-2xl text-primaryColor100 ${(!regionKey || isLoading || (!!regionKey && !hasCities)) ? 'opacity-50' : ''}`}
              ></IonInput>
              <label className="absolute text-xl font-bold top-2 z-10  bg-white  px-2  text-primaryColor100 -translate-y-6 right-10">
                المدينة{" "}
                <span className="text-xl font-bold top-2 z-10 bg-white px-2 text-paragraphColor -translate-y-6 right-10">
                  (اختياري)
                </span>
              </label>
            </div>

            <div className="relative w-full mt-4">
              <IonInput
                readonly={true}
                disabled={!cityKey || isLoading || (!!cityKey && !hasDistricts)}
                placeholder={!cityKey ? "يرجى اختيار المدينة أولاً" : "يرجى اختيار الحي"}
                value={isLoading && !!cityKey && !districtName ? "جاري التحميل..." : districtName}
                onClick={!!cityKey && !isLoading && hasDistricts ? onDistrictClick : undefined}
                className={`w-full border-2 rounded border-paragraphColor text-2xl text-primaryColor100 ${(!cityKey || isLoading || (!!cityKey && !hasDistricts)) ? 'opacity-50' : ''}`}
              ></IonInput>
              <label className="absolute text-xl font-bold top-2 z-10  bg-white  px-2  text-primaryColor100 -translate-y-6 right-10">
                الحي{" "}
                <span className="text-xl font-bold top-2 z-10 bg-white px-2 text-paragraphColor -translate-y-6 right-10">
                  (اختياري)
                </span>
              </label>
            </div>

            <div className="relative w-full mt-4">
              <IonInput
                readonly={true}
                disabled={!districtKey || isLoading || (!!districtKey && !hasPlans)}
                placeholder={!districtKey ? "يرجى اختيار الحي أولاً" : "يرجى اختيار المخطط"}
                value={isLoading && !!districtKey && !planName ? "جاري التحميل..." : planName}
                onClick={!!districtKey && !isLoading && hasPlans ? onPlanClick : undefined}
                className={`w-full border-2 rounded border-paragraphColor text-2xl text-primaryColor100 ${(!districtKey || isLoading || (!!districtKey && !hasPlans)) ? 'opacity-50' : ''}`}
              ></IonInput>
              <label className="absolute text-xl font-bold top-2 z-10  bg-white  px-2  text-primaryColor100 -translate-y-6 right-10">
                المخطط{" "}
                <span className="text-xl font-bold top-2 z-10 bg-white px-2 text-paragraphColor -translate-y-6 right-10">
                  (اختياري)
                </span>
              </label>
            </div>

            <div className="relative w-full mt-4">
              <IonInput
                readonly={true}
                disabled={!planKey || isLoading || (!!planKey && !hasParcels)}
                placeholder={!planKey ? "يرجى اختيار المخطط أولاً" : "يرجى اختيار قطعة الأرض"}
                value={isLoading && !!planKey && !parcelName ? "جاري التحميل..." : parcelName}
                onClick={!!planKey && !isLoading && hasParcels ? onParcelClick : undefined}
                className={`w-full border-2 rounded border-paragraphColor text-2xl text-primaryColor100 ${(!planKey || isLoading || (!!planKey && !hasParcels)) ? 'opacity-50' : ''}`}
              ></IonInput>
              <label className="absolute text-xl font-bold top-2 z-10  bg-white  px-2  text-primaryColor100 -translate-y-6 right-10">
                قطعة الأرض{" "}
                <span className="text-xl font-bold top-2 z-10 bg-white px-2 text-paragraphColor -translate-y-6 right-10">
                  (اختياري)
                </span>
              </label>
            </div>
          </>
        )}
        

        {/* Coordinate Search Tab */}
        {activeTab === "coordinates" && (
          <>
            {/* Coordinate Search Method Selection */}
            <div className="w-full mt-4 mb-4">
              <div className="flex flex-row gap-6 justify-center">
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    id="manual"
                    name="coordinateMethod"
                    value="manual"
                    checked={coordinateSearchMethod === "manual"}
                    onChange={(e) => setCoordinateSearchMethod(e.target.value as "manual" | "googleMaps")}
                    className="w-5 h-5 text-primaryColor100"
                  />
                  <label htmlFor="manual" className="text-lg font-bold text-primaryColor100">
                    الإحداثيات مكتوبة
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    id="googleMaps"
                    name="coordinateMethod"
                    value="googleMaps"
                    checked={coordinateSearchMethod === "googleMaps"}
                    onChange={(e) => setCoordinateSearchMethod(e.target.value as "manual" | "googleMaps")}
                    className="w-5 h-5 text-primaryColor100"
                  />
                  <label htmlFor="googleMaps" className="text-lg font-bold text-primaryColor100">
                    عبر رابط خرائط جوجل
                  </label>
                </div>
              </div>
            </div>

            {/* Written Coordinates Input */}
            {coordinateSearchMethod === "manual" && (
              <>
                <div className="relative w-full mt-4">
                  <IonInput
                    type="number"
                    placeholder="خط الطول (Longitude)"
                    value={longitude}
                    onIonChange={(e) => setLongitude(e.detail.value || "")}
                    className="w-full border-2 rounded border-paragraphColor text-2xl text-primaryColor100"
                  ></IonInput>
                  <label className="absolute text-xl font-bold top-2 z-10 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    خط الطول
                  </label>
                </div>

                <div className="relative w-full mt-4">
                  <IonInput
                    type="number"
                    placeholder="خط العرض (Latitude)"
                    value={latitude}
                    onIonChange={(e) => setLatitude(e.detail.value || "")}
                    className="w-full border-2 rounded border-paragraphColor text-2xl text-primaryColor100"
                  ></IonInput>
                  <label className="absolute text-xl font-bold top-2 z-10 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    خط العرض
                  </label>
                </div>
              </>
            )}

            {/* Google Maps Link Input */}
            {coordinateSearchMethod === "googleMaps" && (
              <div className="relative w-full mt-4">
                <IonInput
                  type="url"
                  placeholder="مثل: https://maps.google.com/?q=24.759492,46.664984"
                  value={googleMapsUrl}
                  onIonChange={(e) => setGoogleMapsUrl(e.detail.value || "")}
                  className="w-full border-2 rounded border-paragraphColor text-2xl text-primaryColor100"
                ></IonInput>
                <label className="absolute text-xl font-bold top-2 z-10 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                  رابط خرائط جوجل
                </label>
              </div>
            )}

            <div className="w-full mt-6 flex gap-3">
              <IonButton
                expand="block"
                fill="solid"
                onClick={handleCoordinateSearch}
                disabled={coordinateLoading || 
                  (coordinateSearchMethod === "manual" && (!longitude || !latitude)) ||
                  (coordinateSearchMethod === "googleMaps" && !googleMapsUrl)
                }
                className="text-white bg-blue-500 hover:bg-blue-600"
                color="primary"
                style={{
                  borderRadius: "8px",
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                }}
              >
                {coordinateLoading ? "جاري البحث..." : 
                  coordinateSearchMethod === "manual" ? "البحث بالإحداثيات المدخلة" : "البحث من رابط خرائط جوجل"
                }
              </IonButton>

              <IonButton
                expand="block"
                fill="solid"
                onClick={() => {
                  setLongitude("");
                  setLatitude("");
                  setGoogleMapsUrl("");
                  setCoordinateData(null);
                }}
                className="text-white bg-gray-500 hover:bg-gray-600"
                color="medium"
                style={{
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                }}
              >
                تفريغ المحتويات
              </IonButton>
            </div>

            {/* Results Section */}
            {coordinateData && (
              <div className="w-full mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-bold text-primaryColor100 mb-4 text-center">
                  تم العثور على بيانات القطعة
                </h3>

                {/* Action Buttons */}
                <div className="flex gap-3 flex-wrap">
                  <IonButton
                    expand="block"
                    fill="solid"
                    onClick={handleGoToCoordinates}
                    style={{
                      "--background": "#0031c4",
                      "--background-hover": "#002599",
                      "--border-radius": "8px",
                      "--border-color": "#0031c4",
                      "--color": "#fff",
                      "--padding-top": "0.75rem",
                      "--padding-bottom": "0.75rem",
                      "--font-size": "1rem",
                      "--font-weight": "bold",
                    }}
                  >
                    الذهاب للإحداثيات
                  </IonButton>
                  <IonButton
                    expand="block"
                    fill="solid"
                    onClick={() => {
                      const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
                      window.open(googleMapsUrl, "_blank");
                    }}
                    style={{
                      "--background": "#ef4444", // softer red
                      "--background-hover": "#dc2626", // darker on hover
                      "--border-radius": "8px",
                      "--border-color": "#dc2626",
                      "--color": "#fff",
                      "--padding-top": "0.75rem",
                      "--padding-bottom": "0.75rem",
                      "--font-size": "1rem",
                      "--font-weight": "bold",
                    }}
                  >
                    الذهاب لخرائط جوجل
                  </IonButton>

                  <IonButton
                    expand="block"
                    fill="solid"
                    onClick={handleOpenInfoModal}
                    style={{
                      "--background": "#22c55e",
                      "--background-hover": "#16a34a",
                      "--border-radius": "8px",
                      "--border-color": "#16a34a",
                      "--color": "#fff",
                      "--padding-top": "0.75rem",
                      "--padding-bottom": "0.75rem",
                      "--font-size": "1rem",
                      "--font-weight": "bold",
                    }}
                  >
                    المزيد من المعلومات
                  </IonButton>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ModalSheet>
  );
};

export default FilterModal;
